import { useState, useEffect } from "react"; // Import useEffect
import { FaTimes, FaRegCalendarAlt } from "react-icons/fa";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { api } from "../../../../../api/notification";
import { api as learningPath } from "../../../../../api/learningPath";
import { useQuery } from "react-query";
import toast from "react-hot-toast";
import Select from "react-select";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { ClipLoader } from "react-spinners";
import ConfirmModal from "../../../../../components/Modal/ConfirmModal";

export default function CreateNotification({
  selectedUser,
  setSelectedUser,
  setOpenNotificationModal,
  refetch,
  refecthSummaryData
}) {
  const isEditMode = !!selectedUser;
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const user = JSON.parse(window.sessionStorage.getItem("user"));
  const orgId = user?.org?.id;
  const [perPage, setPerPage] = useState(10);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [schedule, setSchedule] = useState(
    selectedUser?.dateScheduled ? "later" : ""
  );
  const [selectedCohorts, setSelectedCohorts] = useState([]);
  const [scheduledDate, setScheduleDate] = useState(
    selectedUser?.dateScheduled || null
  );

  const { data: cohorts, isLoading } = useQuery(
    ["cohort", orgId, search, perPage, currentPage],
    async () => {
      const response = await learningPath.getCohort({
        id: orgId,
        search,
        perPage,
        currentPage,
      });
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    },
    {
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const { data: events } = useQuery(
    ["event", orgId, search, perPage, currentPage],
    async () => {
      const response = await api.getEventTriggers();
      return response.data;
    },
    {
      staleTime: 5 * 60 * 1000,
      cacheTime: 30 * 60 * 1000,
    },
    {
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const cohortsOptions = cohorts?.map((item) => ({
    value: item?.cohort.id,
    label: item?.cohort.name,
  }));

  const handleCohortChange = (selectedOptions) => {
    setSelectedCohorts(selectedOptions);
  };

  const handleScheduledDateChange = (date) => {
    const formattedDate = date.toISOString().replace(/:\d\d\.\d+Z$/, "Z");
    setScheduleDate(formattedDate);
  };

  const validationSchema = Yup.object({
    title: Yup.string().required("Title is required"),
    event: Yup.string().required("Trigger event is required"),
    message: Yup.string().required("Message content is required"),
  });

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    const cohortIds = selectedCohorts?.map((cohort) => cohort.value);
    try {
      let response;
      const payload = {
        name: values.title,
        cohortIds: cohortIds,
        trigger: values.event,
        deliveryMethod: "in-app",
        message: values.message,
        dateScheduled:
          schedule === "now" ? new Date().toISOString() : scheduledDate,
      };

      if (isEditMode) {
        response = await api.updateNotificationTemplate({
          notificationId: selectedUser.id,
          orgId: orgId,
          payload,
        });
        toast.success(
          response?.message || "Notification updated successfully!"
        );
        
        setSelectedUser(null);
      } else {
        response = await api.createNotificationTemplate({
          orgId: orgId,
          payload,
        });
       
        toast.success(
          response?.message || "Notification created successfully!"
        );
      }
      // const notificationId = response?.data?.id; // Extract ID from response
      // Call sendNotification if schedule === "now"

      if (schedule === "now") {
        const notificationId = response?.data?.id; // Extract notificationId from the response
        const sendResponse = await api.sendNotification({
          orgId: orgId,
          notificationId: notificationId,
        });
       
        toast.success(sendResponse.message, {
          duration: 5000, // Display the toast for 5 seconds
        });
      }
      // if (schedule === "now" && notificationId) {
      //   try {
      //     const sendResponse = await api.sendNotification({
      //       orgId: orgId,
      //       notificationId: notificationId,
      //     });
      //     toast.success(sendResponse.message, { duration: 5000 });
      //   } catch (error) {
      //     toast.error(
      //       error.response?.data?.message || "Failed to send notification"
      //     );
      //   }
      // }
      refecthSummaryData()
      refetch();
      resetForm();
      setOpenNotificationModal(false);
    } catch (error) {
      alert("Error: " + error.message);
    } finally {
      setSubmitting(false);
    }
  };

  // Add useEffect to handle automatic submission when schedule is set to "now"
  useEffect(() => {
    if (schedule === "now") {
      setOpenConfirmModal(true); // Open confirmation modal
    }
  }, [schedule]);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-30 z-20">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-semibold">
            {isEditMode ? "Edit Notification" : "Create Notification"}
          </h2>
          <button
            onClick={() => setOpenNotificationModal(false)}
            className="text-gray-500 hover:text-gray-700"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        <Formik
          initialValues={{
            title: selectedUser?.name || "",
            event: selectedUser?.trigger || null,
            message: selectedUser?.message || "",
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({
            values,
            setFieldValue,
            isSubmitting,
            submitForm: formikSubmit,
          }) => (
            <Form className="mt-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Title
                </label>
                <Field
                  type="text"
                  name="title"
                  className="w-full border rounded px-3 py-2 focus:ring focus:ring-blue-300"
                  placeholder="Enter title"
                />
                <ErrorMessage
                  name="title"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Audience
                </label>
                <Select
                  isMulti
                  options={cohortsOptions}
                  onChange={handleCohortChange}
                  value={selectedCohorts}
                  placeholder="Select a cohort"
                  className="text-base"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Trigger Event
                </label>
                <Field
                  name="event"
                  component={Select}
                  options={events?.map((event) => ({
                    value: event.id,
                    label: event.name,
                  }))}
                  onChange={(option) => {
                    setFieldValue("event", option.value);
                  }}
                  className="px-3 py-2"
                />
                <ErrorMessage
                  name="event"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>
              {/* Delivery Method */}
              {/* <div>
                <span className="block text-sm font-medium text-gray-700">
                  Delivery Method
                </span>
                <div className="flex space-x-4 mt-2">
                  {[
                    { label: "Email", value: "email" },
                    { label: "SMS", value: "sms" },
                    { label: "Push Notification", value: "in-app" },
                    { label: "Chatbot", value: "chatbot" },
                  ].map(({ label, value }) => (
                    <label key={value} className="flex items-center space-x-2">
                      <Field
                        type="checkbox"
                        name="deliveryMethods"
                        value={value} // Ensure correct value
                        className="form-checkbox"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
                <ErrorMessage
                  name="deliveryMethods"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div> */}

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Message Content
                </label>
                <Field
                  as="textarea"
                  name="message"
                  className="w-full border rounded px-3 py-2 h-24 focus:ring focus:ring-blue-300"
                  placeholder="Type your message..."
                />
                <ErrorMessage
                  name="message"
                  component="div"
                  className="text-red-500 text-sm mt-1"
                />
              </div>

              <div>
                <span className="block text-sm font-medium text-gray-700">
                  Scheduling Options
                </span>
                <div className="flex items-center space-x-4 mt-2">
                  <div>
                    <input
                      type="radio"
                      id="send-now"
                      name="schedule"
                      value="now"
                      checked={schedule === "now"}
                      onChange={(e) => setSchedule(e.target.value)}
                    />
                    <label htmlFor="send-now" className="ml-2">
                      Send Now
                    </label>
                  </div>
                  <div>
                    <input
                      type="radio"
                      id="schedule-later"
                      name="schedule"
                      value="later"
                      checked={schedule === "later"}
                      onChange={(e) => setSchedule(e.target.value)}
                    />
                    <label htmlFor="schedule-later" className="ml-2">
                      Schedule for Later
                    </label>
                  </div>
                </div>

                {schedule === "later" && (
                  <div className="relative mt-2">
                    <DatePicker
                      selected={scheduledDate ? new Date(scheduledDate) : null}
                      onChange={handleScheduledDateChange}
                      showTimeSelect
                      timeFormat="HH:mm"
                      timeIntervals={15}
                      dateFormat="MMMM d, yyyy h:mm aa"
                      timeCaption="time"
                      placeholder="Select date and time"
                      className="border bg-primaryGray p-2 rounded border-gray-300 focus:primary focus:ring-0 focus:ring-offset-2"
                    />
                  </div>
                )}
              </div>

              <div className="flex justify-end items-center mt-6 border-t pt-4">
                <div className="space-x-2">
                  <button
                    type="button"
                    className="bg-gray-200 px-4 py-2 rounded hover:bg-gray-300"
                    onClick={() => setOpenNotificationModal(false)}
                  >
                    Cancel
                  </button>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="bg-primaryBlue hover:opacity-50 text-white  mb-4 rounded-md cursor-pointer font-bold py-2 px-6"
                  >
                    {isSubmitting ? (
                      <div className="flex justify-center">
                        <ClipLoader color="#fff" />
                      </div>
                    ) : isEditMode ? (
                      "Update"
                    ) : (
                      "Create"
                    )}
                  </button>
                </div>
              </div>

              {openConfirmModal && (
                <ConfirmModal
                  title="Confirm Send Now 🚀"
                  description={`Are you sure you want to send this notification now?`}
                  onYes={() => {
                    setOpenConfirmModal(false);
                    formikSubmit(); // Submit the form
                  }}
                  onClose={() => setOpenConfirmModal(false)}
                />
              )}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}
